/**
 * @import { WorkflowFile } from '$lib/types/private';
 */

/**
 * Description the CMS opens an Editorial Workflow pull request or merge request with, which tells a
 * maintainer where it came from.
 */
export const PULL_REQUEST_DESCRIPTION = 'Automatically generated by Sveltia CMS';

/**
 * Create helpers for a title that marks a pull request or merge request as a draft with a prefix,
 * as Gitea/Forgejo and GitLab do in place of a dedicated API field.
 * @param {object} args Arguments.
 * @param {RegExp} args.regex Regular expression matching one draft indicator at the beginning of a
 * title, along with the whitespace around it. It must not have the `g` or `y` flag.
 * @param {string} args.prefix Prefix added to a title to mark it as a draft.
 * @returns {{
 * strip: (title: string) => string,
 * format: (title: string, isDraft: boolean) => string,
 * }} `strip` removes any draft indicator from a raw title; `format` adds the prefix to a title
 * without one if `isDraft` is `true`.
 */
export const createDraftTitleCodec = ({ regex, prefix }) => ({
  /**
   * Remove any draft indicator from the given title.
   * @param {string} title Raw title.
   * @returns {string} Title without a draft prefix.
   */
  strip: (title) => {
    let result = title;

    // Repeat, because a title can carry a combination of indicators, e.g. `Draft: [Draft] Title`
    while (regex.test(result)) {
      result = result.replace(regex, '');
    }

    return result;
  },
  /**
   * Add the draft prefix to the given title if the request is a draft.
   * @param {string} title Title without a draft prefix.
   * @param {boolean} isDraft Whether the request is a draft.
   * @returns {string} Title to send to the API.
   */
  format: (title, isDraft) => (isDraft ? `${prefix}${title}` : title),
});

/**
 * Convert the changed files of a pull request or comparison, as listed by the REST API of GitHub or
 * Gitea/Forgejo, to workflow files.
 * @param {Record<string, any>[]} files Files.
 * @param {object} args Arguments.
 * @param {string} args.removedStatus Status the service gives a removed file: `removed` on GitHub,
 * `deleted` on Gitea/Forgejo.
 * @returns {WorkflowFile[]} Files.
 */
export const parseRestFiles = (files, { removedStatus }) =>
  files.map(({ filename, status, previous_filename: previousPath }) => ({
    path: filename,
    sha: '',
    size: 0,
    deleted: status === removedStatus,
    // Gitea/Forgejo gives a file that wasn’t renamed an empty previous path
    previousPath: previousPath || undefined,
  }));
